import { Button } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import { ButtonRibbon } from "../components/ButtonRibbon";
import NumberInput from "../components/NumberInput";
import { useErrorIfModelNotUpToDate } from "../hooks/queries";
import { checkPermission } from "../state/appState";
import { removeSelected, setSelected } from "../state/existingRasterEditsMenu";
import { Level, remindUserToPressPlay, sendMessage } from "../state/mainGui";
// functions used from layerRasterSourcesMenufrom: retrieveConfiguredRasters, getList, getStatus
// imported * to avoid import conflicts
import {
  AbsoluteOrRelative,
  EditableRaster,
  NewValue,
  PolygonStatus,
  getAbsoluteOrRelative,
  getEditableRaster,
  getNewValue,
  getPoints,
  getStatus,
  setAbsoluteOrRelativeAction,
  setEditableRasterAction,
  setNewValueAction,
  setStatusAction,
} from "../state/polygonEdit";
import { RequestStatus } from "../state/requestStatus";
import { getSimulation, getSimulationStatus, getThreediModel } from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import inputElementStyles from "../styles/InputElements.module.css";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";

import * as layerRasterSourcesMenufrom from "./../state/layerRasterSourcesMenu";
import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import ExistingRasterEdits from "./existing/ExistingRasterEdits";
import ExistingItemsContainer from "./ExistingItemsContainer";
import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";
import menuStyles from "./Menu.module.css";

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;

const RasterEditMenu: React.FC<PropsFromDispatch> = (props) => {
  const intl = useIntl();
  const [editApiCallState, setEditApiCallState] = useState<RequestStatus>("NOT_SENT");

  const selectedEditableraster = useSelector(getEditableRaster);
  const newValue = useSelector(getNewValue);
  const absoluteOrRelative = useSelector(getAbsoluteOrRelative);
  const rasterEditStatus = useSelector(getStatus);
  const simulation = useSelector(getSimulation);
  const threediModel = useSelector(getThreediModel);
  const simulationStatus = useSelector(getSimulationStatus);
  const polygonPoints = useSelector(getPoints);

  const sourceRasters = useSelector(layerRasterSourcesMenufrom.getList);
  const sourceRasterGetStatus = useSelector(layerRasterSourcesMenufrom.getStatus);
  const demRaster = sourceRasters.find((raster) => raster.type === "dem_file");
  const interceptionRaster = sourceRasters.find((raster) => raster.type === "interception_file");
  const disabledBecauseRastersNotPresent =
    !demRaster && !interceptionRaster && sourceRasterGetStatus === "SUCCESS";

  const rasterEditFailedMessage = useErrorIfModelNotUpToDate(
    "Editing raster failed.",
    threediModel
  );

  const permission = useSelector(checkPermission)("editRaster");

  const disableWholeForm =
    disabledBecauseRastersNotPresent || permission.allowed === false || editApiCallState === "SENT";

  const disablesWholeFormErrorMessage =
    (disabledBecauseRastersNotPresent && (
      <FormattedMessage
        id="rasterEditMenu.need_interception_or_dem_raster"
        defaultMessage="Raster edit not available for models without 2D"
      />
    )) ||
    (permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl));

  const translatedOptionDem = intl.formatMessage({
    id: "editRasterMenu.type_elevation_dem",
    defaultMessage: "Elevation (DEM)",
  });
  const translatedOptionInterception = intl.formatMessage({
    id: "editRasterMenu.type_interception",
    defaultMessage: "Interception",
  });

  return (
    <div className={`${CollapsableSidebarStyles.LeftMenu}`}>
      <LeftMenuHeader>
        <FormattedMessage id="editRasterMenu.header" defaultMessage="Edit layers" />
      </LeftMenuHeader>
      <div
        className={`${menuStyles.SubMenu} ${menuStyles.List}`}
        style={{ padding: "0 8px 0 8px" }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <FormLabel>
              <FormattedMessage id="editRasterMenu.type" defaultMessage="Type" />
            </FormLabel>
            <div>
              <select
                value={selectedEditableraster}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  props.setEditableRasterAction(event.target.value as EditableRaster);
                }}
                className={`${inputElementStyles.Input} ${inputElementStyles.Padding}`}
                disabled={disabledBecauseRastersNotPresent}
                // hide for now since only one option is available and show that option as text instead
                style={{ display: "none" }}
              >
                {demRaster ? <option value={"DEM"}>{translatedOptionDem}</option> : null}
                {interceptionRaster ? (
                  <option value={"INTERCEPTION"}>{translatedOptionInterception}</option>
                ) : null}
                {/* will be supprted later <option value={"INFILTRATION"}>Infiltration</option> */}
              </select>
              {/* Since selectbox is hidden, temporarily show only option as plain text */}
              {translatedOptionDem}
            </div>
            <FormLabel>
              <FormattedMessage id="editRasterMenu.elevation" defaultMessage="Elevation" />
            </FormLabel>

            {absoluteOrRelative === "Absolute" ? (
              <NumberInput
                value={newValue}
                unit={<FormattedMessage id="editRasterMenu.unit_mmsl" defaultMessage="mMSL" />}
                setValue={props.setNewValueAction}
                disabled={disabledBecauseRastersNotPresent}
                allowDecimals
                allowNegative
                required
              />
            ) : (
              <NumberInput
                value={newValue}
                unit={<FormattedMessage id="editRasterMenu.unit_m" defaultMessage="m" />}
                setValue={props.setNewValueAction}
                disabled={disabledBecauseRastersNotPresent}
                allowDecimals
                allowNegative
                required
              />
            )}
            <FormLabel>
              <FormattedMessage
                id="editRasterMenu.absoluteOrRelative"
                defaultMessage="Absolute or Relative"
              />
            </FormLabel>
            <select
              className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
              value={absoluteOrRelative}
              onChange={(event) => {
                props.setAbsoluteOrRelativeAction(event.target.value as AbsoluteOrRelative);
              }}
            >
              <option value="Absolute">Absolute</option>
              <option value="Relative">Relative</option>
            </select>

            {rasterEditStatus === "SELECT_OR_NEW" ? (
              <ButtonRibbon>
                <Button
                  variant="secondary"
                  onClick={() => {
                    props.setStatusAction("DRAWING_MODE");
                    props.removeSelected();
                  }}
                >
                  <FormattedMessage
                    id="editRasterMenu.place_on_map_button"
                    defaultMessage="Draw on map"
                  />
                </Button>
              </ButtonRibbon>
            ) : null}
            {rasterEditStatus === "DRAWING_MODE" ? (
              <>
                <ButtonRibbon>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      props.setStatusAction("SELECT_OR_NEW");
                    }}
                  >
                    <FormattedMessage
                      id="editRasterMenu.button_cancel_drawing_mode"
                      defaultMessage="Cancel"
                    />
                  </Button>
                </ButtonRibbon>
                <div className={menuStyles.ListItem}>
                  <span>
                    <FormattedMessage
                      id="dischargeMenu.drawing_mode_place_cursor_on_map_to_draw"
                      defaultMessage="Place your cursor on the map to draw the item"
                    />
                  </span>
                </div>
              </>
            ) : null}
            {rasterEditStatus === "NEED_CONFIRMATION" ? (
              <>
                <ButtonRibbon>
                  <Button
                    variant="primary"
                    onClick={(e) => {
                      const target = e.target as HTMLButtonElement;
                      if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                        const v3Api = getV3Api();
                        if (polygonPoints === null) {
                          // todo error handling to user needed? Or can this never happen
                          console.error(
                            "No polygonpoints therefore not possible to send polygonpoints to api. polygonPoints of: ",
                            polygonPoints
                          );
                          return;
                        } else if (simulation === null) {
                          // todo error handling to user needed? Or can this never happen
                          console.error(
                            "No simulation therefore not possible to send polygonpoints to api. simulation of: ",
                            simulation
                          );
                          return;
                        }
                        const rasterEditSimulationId = simulation.id;
                        if (rasterEditSimulationId === undefined) return;
                        const polygonPointsArrayAsStrings = polygonPoints.map((item) => {
                          return "[" + item[0] + ", " + item[1] + "]";
                        });
                        polygonPointsArrayAsStrings.push(polygonPointsArrayAsStrings[0]);
                        const polygonPointsArrayAsString = polygonPointsArrayAsStrings.join(",");
                        const geojsonPolygonString =
                          '{"type":"Polygon","coordinates":[[' + polygonPointsArrayAsString + "]]}";
                        let rasterId = "";
                        if (selectedEditableraster === "DEM") {
                          rasterId = (demRaster && demRaster.id + "") || "";
                        } else if (selectedEditableraster === "INTERCEPTION") {
                          rasterId = (interceptionRaster && interceptionRaster.id + "") || "";
                        }
                        setEditApiCallState("SENT");
                        const simulationsEventsRasterEditsCreateRequest: api.SimulationsEventsRasterEditsCreateRequest =
                          {
                            simulationPk: rasterEditSimulationId,
                            data: {
                              raster: rasterId, //string,
                              offset: simulationStatus.time, //number;
                              value: parseFloat(newValue + ""), // number;
                              polygon: JSON.parse(geojsonPolygonString),
                              relative: absoluteOrRelative === "Relative",
                            },
                          };

                        v3Api
                          .simulationsEventsRasterEditsCreate(
                            simulationsEventsRasterEditsCreateRequest
                          )
                          .then((_response) => {
                            setEditApiCallState("SUCCESS");
                            props.setStatusAction("SELECT_OR_NEW");
                          })
                          .catch((error) => {
                            setEditApiCallState("FAILED");
                            console.error("raster edit failed with following error", error);
                            props.sendMessage(rasterEditFailedMessage, 25000, "error");
                            props.setStatusAction("SELECT_OR_NEW");
                          });
                        props.setStatusAction("SELECT_OR_NEW");
                        props.remindUserToPressPlay();
                      }
                    }}
                    isDisabled={editApiCallState === "SENT"}
                  >
                    <FormattedMessage
                      id="editRasterMenu.button_confirm drawing"
                      defaultMessage="Confirm"
                    />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      props.setStatusAction("DRAWING_MODE");
                    }}
                  >
                    <FormattedMessage
                      id="editRasterMenu.button_redo_polygon"
                      defaultMessage="Edit drawing"
                    />
                  </Button>
                </ButtonRibbon>
                <ButtonRibbon>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      props.setStatusAction("SELECT_OR_NEW");
                    }}
                  >
                    <FormattedMessage
                      id="editRasterMenu.button_cancel_drawing_mode"
                      defaultMessage="Cancel"
                    />
                  </Button>
                </ButtonRibbon>
              </>
            ) : null}
            <div className={`${menuStyles.ListItem} `}>
              {disablesWholeFormErrorMessage ? (
                <span className={ErrorMessageStyles.ErrorMessageStyle}>
                  {disablesWholeFormErrorMessage}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
      <ExistingItemsContainer>
        <ExistingRasterEdits />
      </ExistingItemsContainer>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setEditableRasterAction: (editableRaster: EditableRaster) =>
      dispatch(setEditableRasterAction(editableRaster)),
    setNewValueAction: (newValue: NewValue) => dispatch(setNewValueAction(newValue)),
    setAbsoluteOrRelativeAction: (absoluteOrRelative: AbsoluteOrRelative) =>
      dispatch(setAbsoluteOrRelativeAction(absoluteOrRelative)),
    setStatusAction: (status: PolygonStatus) => dispatch(setStatusAction(status)),
    sendMessage: (message: string | JSX.Element, timeoutMs: number, level?: Level) =>
      dispatch(sendMessage(message, timeoutMs, level)),
    setSelected: (selectedUid: string | null) => dispatch(setSelected(selectedUid)),
    removeSelected: () => dispatch(removeSelected()),
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(RasterEditMenu);
